import { createDrawingCanvas, getDrawingContext2d } from '@/core/drawing-canvas'
import { performanceDiagnostics } from './performance-diagnostics'

export interface SourceFrame {
  readonly id: number
  readonly capturedAt: number
  /** Browser capture/presentation time, retained separately for evidence age. */
  readonly sourceCapturedAt?: number
  readonly observedAt?: number
  readonly timestampSource?: 'capture' | 'presentation' | 'observed'
}

type FirefoxVideoElement = HTMLVideoElement & {
  webkitDecodedFrameCount?: number
  mozPresentedFrames?: number
}

function usableFrameCount(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

export interface VideoFrameCounters {
  totalVideoFrames: number | null
  decodedFrames: number | null
  mozPresentedFrames: number | null
}

export function readVideoFrameCounters(video: HTMLVideoElement): VideoFrameCounters {
  let totalVideoFrames: number | null = null
  try { totalVideoFrames = usableFrameCount(video.getVideoPlaybackQuality?.().totalVideoFrames) } catch { /* optional */ }
  const element = video as FirefoxVideoElement
  return { totalVideoFrames, decodedFrames: usableFrameCount(element.webkitDecodedFrameCount),
    mozPresentedFrames: usableFrameCount(element.mozPresentedFrames) }
}

/** One identity across regular and priority sampling; retains metadata only. */
export class VideoFrameSource {
  private readonly releaseDiagnostic = performanceDiagnostics.acquire('frame-sources')
  private callbackId: number | null = null
  private disposed = false
  private counters: VideoFrameCounters = { totalVideoFrames: null, decodedFrames: null, mozPresentedFrames: null }
  private presented: number | null = null
  private fingerprint: number | null = null
  private current: SourceFrame | null = null
  private readonly startedAt: number
  private lastCallbackAt: number | null = null
  private lastFingerprintAt = -Infinity
  private fallbackSinceCallback = false
  private bootstrapPending = false
  private readonly listeners = new Set<() => void>()

  constructor(private readonly video: HTMLVideoElement, private readonly fallbackDelayMs: () => number = () => 500,
    private readonly options: { now?: () => number; onFrame?: () => void; skipInitialFrame?: boolean; frameFingerprint?: () => number | null } = {}) {
    this.startedAt = this.now()
    this.bootstrapPending = options.skipInitialFrame === true
    this.read()
    this.subscribe()
  }

  listen(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  diagnostic() {
    return { ...this.counters, presentedFrames: this.presented, lastVideoFrameAt: this.lastCallbackAt,
      lastSourceFrameAt: this.current?.observedAt ?? null, sourceFrameId: this.current?.id ?? null }
  }

  read(): SourceFrame | null {
    if (this.disposed || !this.video.videoWidth || !this.video.videoHeight) return null
    const counters = readVideoFrameCounters(this.video)
    if (this.bootstrapPending) {
      // A replacement video can acquire its first buffered image after construction.
      if (Object.values(counters).some(value => value !== null) || this.video.readyState >= 2) {
        this.bootstrapPending = false
        this.counters = counters
        this.sampleFingerprint()
        this.fallbackSinceCallback = true
      }
      return null
    }
    const moved = this.countersMoved(counters)
    this.counters = counters
    if (moved) {
      this.advance()
      this.fallbackSinceCallback = true
      this.fingerprint = null
    }
    const previous = this.fingerprint
    const reference = this.current?.observedAt ?? this.startedAt
    const fallbackAllowed = this.now() - reference >= Math.max(500, this.fallbackDelayMs())
      || !this.video.requestVideoFrameCallback && !this.current && !this.options.skipInitialFrame
    const sampled = (moved || previous === null || fallbackAllowed) && this.sampleFingerprint()
    // A stationary positive counter is not proof that the drawable image froze.
    if (!moved && sampled && fallbackAllowed
      && this.fingerprint !== null && this.fingerprint !== previous
      && (previous !== null || !this.video.requestVideoFrameCallback && !this.options.skipInitialFrame)) {
      this.advance()
      this.fallbackSinceCallback = true
    }
    return this.current
  }

  private sampleFingerprint(): boolean {
    if (this.now() - this.lastFingerprintAt < 500) return false
    this.lastFingerprintAt = this.now()
    this.fingerprint = this.options.frameFingerprint?.() ?? this.imageFingerprint()
    return true
  }

  private countersMoved(next: VideoFrameCounters): boolean {
    return (Object.keys(next) as (keyof VideoFrameCounters)[]).some(key => next[key] !== null && next[key] !== this.counters[key])
  }

  async waitAfter(id: number, signal: AbortSignal,
    refresh?: { request: (signal: AbortSignal) => Promise<unknown>; intervalMs: () => number }): Promise<SourceFrame | null> {
    const controller = new AbortController()
    const abort = () => controller.abort()
    signal.addEventListener('abort', abort, { once: true })
    let pending = false, lastRefreshAt = -Infinity
    try {
      while (!signal.aborted && !this.disposed) {
        const frame = this.read()
        if (frame && frame.id > id) return frame
        const now = this.now(), interval = refresh?.intervalMs() ?? 1000
        if (refresh && !pending && now - lastRefreshAt >= Math.max(1000, interval)
          && now - (this.current?.observedAt ?? this.startedAt) >= Math.max(1000, interval + 500)) {
          pending = true; lastRefreshAt = now
          void Promise.resolve().then(() => controller.signal.aborted ? undefined : refresh.request(controller.signal))
            .catch(() => undefined).finally(() => { pending = false })
        }
        await new Promise<void>((resolve) => {
          const done = () => { clearTimeout(timer); signal.removeEventListener('abort', done); resolve() }
          const timer = setTimeout(done, 50)
          signal.addEventListener('abort', done, { once: true })
        })
      }
      return null
    } finally { signal.removeEventListener('abort', abort); controller.abort() }
  }

  dispose(): void {
    this.releaseDiagnostic()
    this.disposed = true
    this.listeners.clear()
    if (this.callbackId !== null) this.video.cancelVideoFrameCallback?.(this.callbackId)
    this.callbackId = null
  }

  private subscribe(): void {
    if (this.disposed || !this.video.requestVideoFrameCallback) return
    this.callbackId = this.video.requestVideoFrameCallback((_now, metadata) => {
      this.callbackId = null
      if (this.disposed) return
      const counters = readVideoFrameCounters(this.video)
      const presented = usableFrameCount(metadata?.presentedFrames)
      const countMoved = this.countersMoved(counters)
      const hasCounter = Object.values(counters).some(value => value !== null)
      const fallbackAlreadySawFrame = this.fallbackSinceCallback && !countMoved
      const presentedMoved = presented !== null && presented !== this.presented
        && (!hasCounter || this.presented !== null)
      const identityMoved = countMoved || presentedMoved || (!hasCounter && presented === null)
      if (this.bootstrapPending) {
        this.bootstrapPending = false
        this.sampleFingerprint()
      } else if (!fallbackAlreadySawFrame && identityMoved) {
        this.advance(metadata)
        if (this.lastFingerprintAt < this.now()) this.fingerprint = null
      } else if (fallbackAlreadySawFrame && this.current) {
        const timing = this.sourceTiming(metadata)
        // Late metadata may make evidence older, never younger.
        if (timing.timestampSource !== 'observed'
          && timing.sourceCapturedAt <= (this.current.sourceCapturedAt ?? this.current.capturedAt)) {
          this.current = { ...this.current, ...timing }
        }
      }
      this.counters = counters
      this.presented = presented
      this.lastCallbackAt = this.now()
      this.fallbackSinceCallback = false
      this.subscribe()
      this.options.onFrame?.()
      for (const listener of this.listeners) listener()
    })
  }

  private now(): number { return this.options.now?.() ?? Date.now() }

  private advance(metadata?: VideoFrameCallbackMetadata): void {
    const observedAt = this.now()
    this.current = { id: (this.current?.id ?? 0) + 1,
      capturedAt: Math.max(observedAt, (this.current?.capturedAt ?? 0) + 1),
      observedAt, ...this.sourceTiming(metadata) }
  }

  private sourceTiming(metadata?: VideoFrameCallbackMetadata): Pick<SourceFrame, 'sourceCapturedAt' | 'timestampSource'> & { sourceCapturedAt: number } {
    const observedAt = this.now(), monotonicNow = performance.now()
    const captureTime = (metadata as (VideoFrameCallbackMetadata & { captureTime?: number }) | undefined)?.captureTime
    const valid = (value: number | undefined): value is number => value !== undefined && Number.isFinite(value) && value >= 0 && value <= monotonicNow
    const timestampSource = valid(captureTime) ? 'capture' : valid(metadata?.presentationTime) ? 'presentation' : 'observed'
    const sourceTime = timestampSource === 'capture' ? captureTime! : timestampSource === 'presentation' ? metadata!.presentationTime : monotonicNow
    // Convert browser monotonic metadata to the consumer's clock. Reading the
    // same buffered frame later must never make its evidence younger.
    return { sourceCapturedAt: observedAt - (monotonicNow - sourceTime), timestampSource }
  }

  private imageFingerprint(): number | null {
    try {
      const canvas = createDrawingCanvas(16, 9)
      const context = getDrawingContext2d(canvas, { willReadFrequently: true })
      context.drawImage(this.video, 0, 0, 16, 9)
      const bytes = context.getImageData(0, 0, 16, 9).data
      let hash = 2_166_136_261
      for (const byte of bytes) hash = Math.imul(hash ^ byte, 16_777_619)
      return hash >>> 0
    } catch { return null }
  }
}
